import { useState, FormEvent } from 'react';

// Point this at your own form backend (Formspree, EmailJS, a custom API route, etc)
// by setting VITE_CONTACT_FORM_URL in your .env file. See .env.example.
const FORM_URL = import.meta.env.VITE_CONTACT_FORM_URL as string | undefined;

function submitViaMailto(data: FormData) {
  const name = data.get('name')?.toString() ?? '';
  const email = data.get('email')?.toString() ?? '';
  const company = data.get('company')?.toString() ?? '';
  const subject = data.get('subject')?.toString() ?? 'Website inquiry';
  const message = data.get('message')?.toString() ?? '';

  const body = [
    `Name: ${name}`,
    `Email: ${email}`,
    company ? `Company: ${company}` : null,
    '',
    message,
  ]
    .filter((line) => line !== null)
    .join('\n');

  const mailto = `mailto:info@devstander.com?subject=${encodeURIComponent(
    `Contact form: ${subject}`
  )}&body=${encodeURIComponent(body)}`;
  window.location.href = mailto;
}

export default function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const message = data.get('message') as string;
    if (message && message.length > 500) {
      return;
    }

    // No backend configured: fall back to opening the visitor's email client
    // with a pre-filled message rather than silently failing.
    if (!FORM_URL) {
      submitViaMailto(data);
      setStatus('success');
      form.reset();
      setCharCount(0);
      return;
    }

    setStatus('loading');
    try {
      const body = new URLSearchParams();
      data.forEach((value, key) => body.append(key, value.toString()));
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="bg-[#0C1220] border border-[#1A2740] rounded-xl p-8 lg:p-10">
      <h2 className="font-display font-bold text-xl text-white mb-2">Send Us a Message</h2>
      <p className="text-slate-400 text-sm mb-8">
        Fill out the form and we&apos;ll get back to you within 24 hours.
      </p>

      {status === 'success' ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-14 h-14 flex items-center justify-center bg-[#3B82F6]/15 rounded-full mb-4">
            <i className="ri-check-line text-[#3B82F6] text-2xl"></i>
          </div>
          <h3 className="font-semibold text-white text-lg mb-2">Message Sent!</h3>
          <p className="text-slate-400 text-sm">
            Thank you for reaching out. We&apos;ll be in touch soon.
          </p>
          <button
            onClick={() => setStatus('idle')}
            className="mt-6 text-[#3B82F6] hover:text-white text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            Send another message
          </button>
        </div>
      ) : (
        <form
          id="contact-form-main"
          onSubmit={handleSubmit}
          className="flex flex-col gap-5"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="name" className="block text-slate-300 text-xs font-semibold uppercase tracking-wider mb-2">
                Full Name <span className="text-[#3B82F6]">*</span>
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                placeholder="John Smith"
                className="w-full bg-[#070B14] border border-[#1A2740] focus:border-[#3B82F6] text-white placeholder-slate-600 text-sm rounded-lg px-4 py-3 outline-none transition-colors duration-200"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-slate-300 text-xs font-semibold uppercase tracking-wider mb-2">
                Email Address <span className="text-[#3B82F6]">*</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                placeholder="john@company.com"
                className="w-full bg-[#070B14] border border-[#1A2740] focus:border-[#3B82F6] text-white placeholder-slate-600 text-sm rounded-lg px-4 py-3 outline-none transition-colors duration-200"
              />
            </div>
          </div>

          <div>
            <label htmlFor="company" className="block text-slate-300 text-xs font-semibold uppercase tracking-wider mb-2">
              Company
            </label>
            <input
              id="company"
              name="company"
              type="text"
              placeholder="Your company name"
              className="w-full bg-[#070B14] border border-[#1A2740] focus:border-[#3B82F6] text-white placeholder-slate-600 text-sm rounded-lg px-4 py-3 outline-none transition-colors duration-200"
            />
          </div>

          <div>
            <label htmlFor="subject" className="block text-slate-300 text-xs font-semibold uppercase tracking-wider mb-2">
              Subject <span className="text-[#3B82F6]">*</span>
            </label>
            <select
              id="subject"
              name="subject"
              required
              className="w-full bg-[#070B14] border border-[#1A2740] focus:border-[#3B82F6] text-white text-sm rounded-lg px-4 py-3 outline-none transition-colors duration-200 cursor-pointer"
              defaultValue=""
            >
              <option value="" disabled>Select a topic</option>
              <option value="Web Development">Web Development</option>
              <option value="Mobile App Development">Mobile App Development</option>
              <option value="Cloud & DevOps">Cloud &amp; DevOps</option>
              <option value="Software Architecture">Software Architecture</option>
              <option value="Long-term Partnership">Long-term Partnership</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="message" className="block text-slate-300 text-xs font-semibold uppercase tracking-wider">
                Message <span className="text-[#3B82F6]">*</span>
              </label>
              <span className={`text-xs ${charCount > 480 ? 'text-red-400' : 'text-slate-500'}`}>
                {charCount}/500
              </span>
            </div>
            <textarea
              id="message"
              name="message"
              required
              rows={5}
              maxLength={500}
              placeholder="Tell us about your project, goals, and timeline..."
              onChange={(e) => setCharCount(e.target.value.length)}
              className="w-full bg-[#070B14] border border-[#1A2740] focus:border-[#3B82F6] text-white placeholder-slate-600 text-sm rounded-lg px-4 py-3 outline-none transition-colors duration-200 resize-none"
            />
          </div>

          {status === 'error' && (
            <p className="text-red-400 text-sm">
              Something went wrong. Please try again or email us directly.
            </p>
          )}

          <button
            type="submit"
            disabled={status === 'loading'}
            className="inline-flex items-center justify-center gap-2 bg-[#3B82F6] hover:bg-[#2563EB] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3.5 rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            {status === 'loading' ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Sending...
              </>
            ) : (
              <>
                Send Message
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-send-plane-line"></i>
                </span>
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
